import { AccountType } from "@shared/contexts";
import { OAuth2Type } from "@shared/contexts";
import { getDeviceInfo } from "@shared/utils";
import { GoogleAuthenticationCommitRequest, OpenAPI, RedirectionResponse, SuccessResponse, SuccessTokenStepResponse } from "@shared/services";
import { request as __request } from "@shared/services/auth/request";
import { withAntiBotTokenRetry, resolveAntiBotToken } from "@shared/utils/recaptchaTokenHandler";

export async function StartTransactionRequest(
  oauth2Data: OAuth2Type,
  antiBotToken?: string
): Promise<SuccessTokenStepResponse> {
  return withAntiBotTokenRetry(
    async (oauth2: OAuth2Type, botToken?: string) => {
      const deviceInfos = await getDeviceInfo();

      const startTransactionDatas = {
        device_info: deviceInfos,
        client: oauth2,
      };

      OpenAPI.TOKEN = undefined;

      // Pass anti-bot token directly through options.headers
      // For start_transaction, X-Anti-Bot is required even without Bearer token
      const resolvedToken = await resolveAntiBotToken(undefined);
      const headers = { 'X-Anti-Bot': resolvedToken };

      return await __request<SuccessTokenStepResponse>(OpenAPI, {
        method: "POST",
        url: "/account/register/start_transaction",
        body: startTransactionDatas,
        mediaType: "application/json",
        headers,
      });
    },
    [oauth2Data, antiBotToken],
    antiBotToken,
    (params, newToken) => [params[0], newToken]
  );
}

export async function RegisterPersonalInformationRequest(
  accountData: AccountType,
  gender: string,
  pronoun: string,
  birthdate: string,
  token: string | undefined,
  antiBotToken?: string
): Promise<SuccessTokenStepResponse> {
  return withAntiBotTokenRetry(
    async (account: AccountType, gen: string, pron: string, bdate: string, authToken: string | undefined, botToken?: string) => {
      const personalInfoDatas = {
        first_name: account.name,
        last_name: account.surname,
        terms: Boolean(account.acceptTerms),
        gender: gen,
        pronoun: pron, 
        birthdate: bdate,
        nationality: account.nationality, 
      };

      OpenAPI.TOKEN = authToken;

      // Pass anti-bot token directly through options.headers
      // X-Anti-Bot should be sent if antiBotToken is available, regardless of Bearer token
      const resolvedToken = await resolveAntiBotToken(undefined);
      const headers = { 'X-Anti-Bot': resolvedToken };

      return await __request<SuccessTokenStepResponse>(OpenAPI, {
        method: "POST",
        url: "/account/register/personal_information",
        body: personalInfoDatas,
        mediaType: "application/json",
        headers,
      });
    },
    [accountData, gender, pronoun, birthdate, token, antiBotToken],
    antiBotToken,
    (params, newToken) => [params[0], params[1], params[2], params[3], params[4], newToken]
  );
}

export async function RegisterIdentityRequest(
  email: string,
  token: string | undefined,
  antiBotToken?: string
): Promise<SuccessTokenStepResponse> {
  return withAntiBotTokenRetry(
    async (emailAddr: string, authToken: string | undefined, botToken?: string) => {
      const identityDatas = {
        email: emailAddr,
      };

      OpenAPI.TOKEN = authToken;

      // Pass anti-bot token directly through options.headers
      // X-Anti-Bot should be sent if antiBotToken is available, regardless of Bearer token
      const resolvedToken = await resolveAntiBotToken(undefined);
      const headers = { 'X-Anti-Bot': resolvedToken };

      return await __request<SuccessTokenStepResponse>(OpenAPI, {
        method: "POST",
        url: "/account/register/identity",
        body: identityDatas,
        mediaType: "application/json",
        headers,
      });
    },
    [email, token, antiBotToken],
    antiBotToken,
    (params, newToken) => [params[0], params[1], newToken]
  );
}

export async function RegisterPasswordRequest(
  password: string, 
  passwordConfirmation: string,
  token: string | undefined,
  antiBotToken?: string
): Promise<SuccessTokenStepResponse> {
  return withAntiBotTokenRetry(
    async (pwd: string, pwdConfirmation: string, authToken: string | undefined, botToken?: string) => {
      const passwordDatas = {
        password: pwd,
        password_confirmation: pwdConfirmation,
      };

      OpenAPI.TOKEN = authToken;

      // Pass anti-bot token directly through options.headers
      // X-Anti-Bot should be sent if antiBotToken is available, regardless of Bearer token
      const resolvedToken = await resolveAntiBotToken(undefined);
      const headers = { 'X-Anti-Bot': resolvedToken };

      return await __request<SuccessTokenStepResponse>(OpenAPI, {
        method: "POST",
        url: "/account/register/password",
        body: passwordDatas,
        mediaType: "application/json",
        headers,
      });
    },
    [password, passwordConfirmation, token, antiBotToken],
    antiBotToken,
    (params, newToken) => [params[0], params[1], params[2], newToken]
  );
}

export async function SendConfirmationCodePost(
  token: string | undefined,
  antiBotToken?: string
): Promise<SuccessResponse> {
  return withAntiBotTokenRetry(
    async (authToken: string | undefined, botToken?: string) => {
      const resolvedToken = await resolveAntiBotToken(undefined);
      const headers: Record<string, string> = { 'X-Anti-Bot': resolvedToken };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      return await __request<SuccessResponse>(OpenAPI, {
        method: "POST",
        url: "/account/register/send_confirmation_code_email",
        mediaType: "application/json",
        headers,
      });
    },
    [token, antiBotToken],
    antiBotToken,
    (params, newToken) => [params[0], newToken]
  );
}

export async function RegisterCommitPost(
  confirmationCode: string,
  token: string | undefined,
  antiBotToken?: string
): Promise<SuccessResponse | RedirectionResponse> {
  return withAntiBotTokenRetry(
    async (code: string, authToken: string | undefined, botToken?: string) => {
      const commitDatas = {
        confirmation_code: parseInt(code),
      };

      OpenAPI.TOKEN = authToken;

      // Pass anti-bot token directly through options.headers
      // X-Anti-Bot should be sent if antiBotToken is available, regardless of Bearer token
      const resolvedToken = await resolveAntiBotToken(undefined);
      const headers = { 'X-Anti-Bot': resolvedToken };

      return await __request<SuccessResponse>(OpenAPI, {
        method: "POST",
        url: "/account/register/commit",
        body: commitDatas,
        mediaType: "application/json",
        headers,
      });
    },
    [confirmationCode, token, antiBotToken],
    antiBotToken,
    (params, newToken) => [params[0], params[1], newToken]
  );
}

export async function CommitOnBoardingPost(
  onBoardingData: GoogleAuthenticationCommitRequest,
  state: string | undefined,
  antiBotToken?: string,
  provider: 'google' | 'facebook' = 'google'
): Promise<RedirectionResponse> {
  return withAntiBotTokenRetry(
    async (onBoardData: GoogleAuthenticationCommitRequest, stateValue: string | undefined, botToken?: string, oauthProvider: 'google' | 'facebook' = 'google') => {
      const personalInfoDatas: GoogleAuthenticationCommitRequest = {
        terms: onBoardData.terms,
      };

      OpenAPI.TOKEN = stateValue;

      const resolvedToken = await resolveAntiBotToken(undefined);
      const headers = { 'X-Anti-Bot': resolvedToken };

      return await __request<RedirectionResponse>(OpenAPI, {
        method: "POST",
        url: `/oauth2/oidc/${oauthProvider}/commit`,
        body: personalInfoDatas,
        mediaType: "application/json",
        headers,
      });
    },
    [onBoardingData, state, antiBotToken, provider],
    antiBotToken,
    (params, newToken) => [params[0], params[1], newToken, params[3]]
  );
}